import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AppBar from '../components/AppBar'
import SubmitButton from '../components/SubmitButton'
import { isApp } from '../utils/platform'
import { useAuthStore } from '../stores/authStore'
import { userApi } from '../api/user'
import { reportNotificationPermission } from '../utils/fcmRegister'

/**
 * 신규 가입 권한 안내 페이지 (아이디/이름 입력 직후).
 * "다음으로"를 누르면 실제 OS 권한 팝업(알림 → 연락처)을 순차로 띄운다(iOS·Android).
 *  - 연락처 허용 → 연락처 데이터 저장 후 /contact-friends(친구 추천)
 *  - 연락처 거부 → 바로 /onboarding-rank(가본 곳 랭킹 온보딩)
 * (연락처 연동 페이지 #9는 이 흐름에서 제거되고, 그 역할을 이 페이지가 대신한다.)
 */
const PermissionRequest: React.FC = () => {
  const navigate = useNavigate()
  const { setContactSyncDone } = useAuthStore()
  const [loading, setLoading] = useState(false)
  const doneRef = useRef(false) // 중복 이동 방지
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null) // 브리지 콜백 미도착 시 안전 이탈

  // 연락처 허용 → 친구 추천으로
  const finishGranted = async (payload: {
    contacts: any[]
    offset: number
    total: number
  }) => {
    if (doneRef.current) return
    doneRef.current = true
    localStorage.setItem('contacts_data', JSON.stringify(payload))
    setContactSyncDone()
    try {
      await userApi.updateContactConsent(true)
    } catch (e) {
      console.error('연락처 동의 저장 실패:', e)
    }
    navigate('/contact-friends', { replace: true })
  }

  // 연락처 거부(또는 브리지 없음) → 랭킹 온보딩으로
  const finishDenied = async () => {
    if (doneRef.current) return
    doneRef.current = true
    setContactSyncDone()
    try {
      await userApi.updateContactConsent(false)
    } catch (e) {
      console.error('연락처 동의 저장 실패:', e)
    }
    navigate('/onboarding-rank', { replace: true })
  }

  // 안전 타임아웃(단계별): 알림/연락처 각 단계에서 네이티브 콜백이 이 시간 내 안 오면
  // 온보딩으로 안전 이탈. 콜백이 올 때마다 리셋 → "한 단계가 무응답"일 때만 걸려서
  // 팝업을 천천히 누르는 사용자도 안 잘린다.
  const STEP_TIMEOUT_MS = 8000
  const armTimeout = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      if (doneRef.current) return
      doneRef.current = true
      setContactSyncDone()
      navigate('/onboarding-rank', { replace: true })
    }, STEP_TIMEOUT_MS)
  }

  // 웹 환경이면 권한 개념이 없으므로 곧장 온보딩으로
  useEffect(() => {
    if (!isApp()) {
      navigate('/onboarding-rank', { replace: true })
    }
  }, [navigate])

  // Flutter 브리지 콜백 등록
  useEffect(() => {
    if (!isApp() || !(window as any).FlutterBridge) return
    const bridge = (window as any).FlutterBridge

    // 연락처 데이터 수신 → 허용 처리
    bridge.onContactsData = (data: any) => {
      if (data && Array.isArray(data.contacts)) {
        finishGranted({
          contacts: data.contacts,
          offset: data.offset ?? 0,
          total: data.total ?? data.contacts.length,
        })
      } else {
        finishDenied()
      }
    }

    // 연락처 권한 결과 → 거부면 온보딩으로(허용이면 onContactsData 대기)
    bridge.onContactsPermissionResult = (data: any) => {
      if (!data || !data.granted) {
        finishDenied()
      } else {
        armTimeout() // 연락처 허용 → 데이터(onContactsData) 대기 리셋
      }
    }

    // 알림 권한 결과 → 서버에 남기고(허용 수 집계용), 허용/거부 관계없이 이어서 연락처 권한 요청
    bridge.onNotificationPermissionResult = (data: any) => {
      reportNotificationPermission(data?.granted, 'signup')
      armTimeout() // 알림 통과 → 연락처 단계 대기 리셋
      proceedContacts()
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      delete bridge.onContactsData
      delete bridge.onContactsPermissionResult
      delete bridge.onNotificationPermissionResult
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 연락처 OS 권한 요청(브리지 없으면 스킵)
  const proceedContacts = () => {
    const bridge = (window as any).FlutterBridge
    if (bridge && typeof bridge.requestContactsPermission === 'function') {
      bridge.requestContactsPermission()
    } else {
      finishDenied()
    }
  }

  const handleNext = () => {
    if (loading || doneRef.current) return
    const bridge = (window as any).FlutterBridge
    if (!isApp() || !bridge) {
      finishDenied()
      return
    }
    setLoading(true)
    armTimeout() // 알림 결과 대기 시작
    // 알림 팝업 먼저 → 그 결과 콜백에서 연락처 팝업.
    // 알림 브리지가 아직 없는(구) 네이티브면 바로 연락처만 요청.
    if (typeof bridge.requestNotificationPermission === 'function') {
      // 신규 유저는 여기서 알림 권한을 요청 → 홈(기존유저용 1회 요청)과 중복 방지 플래그
      try {
        localStorage.setItem('notif_perm_requested_v1', '1')
      } catch {
        /* noop */
      }
      bridge.requestNotificationPermission()
    } else {
      proceedContacts()
    }
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <div className="app-container flex-1 flex flex-col overflow-hidden pb-32px">
        <AppBar />

        <div className="flex-1 overflow-y-auto scrollbar-hide px-20px pt-40px">
          <p className="font-pretendard font-bold text-22px leading-135% text-brown-80%-main">
            우선 몇 가지 권한이 필요해요
          </p>

          <div className="mt-40px flex flex-col gap-12px">
            {/* 알림 (팝업도 알림 먼저) */}
            <div
              className="w-full h-80px rounded-8px p-16px flex items-center gap-16px"
              style={{ backgroundColor: '#E8E1D9' }}
            >
              <img
                src="/images/bell-icon.png"
                alt="알림"
                className="w-28px h-28px object-contain flex-shrink-0"
              />
              <div className="flex flex-col">
                <span className="font-pretendard font-regular text-16px leading-135% text-brown-80%-main">
                  알림
                </span>
                <span className="font-pretendard font-regular text-12px leading-135% text-textColorText2 mt-2px">
                  친구 소식과 혜택을 알려드려요
                </span>
              </div>
            </div>

            {/* 연락처 */}
            <div
              className="w-full h-80px rounded-8px p-16px flex items-center gap-16px"
              style={{ backgroundColor: '#E8E1D9' }}
            >
              <span className="w-28px h-28px flex items-center justify-center flex-shrink-0">
                <svg
                  viewBox="0 0 24 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  preserveAspectRatio="none"
                  aria-label="연락처"
                  style={{
                    width: '25.15px',
                    height: '22.86px',
                    transform: 'rotate(8deg)',
                  }}
                >
                <path
                  d="M15.7203 17.1159L16.3805 16.6434L15.2112 15.1512L14.5536 15.6241L15.7203 17.1159ZM18.3657 16.6852L20.6272 18.2997L21.767 16.8151L19.5065 15.202L18.3657 16.6852ZM20.7409 20.8915L18.6846 22.3659L19.8515 23.8564L21.9076 22.3833L20.7409 20.8915ZM17.5007 22.766C15.6257 22.6723 10.8579 21.8512 6.36541 16.1805L4.83157 17.2915C9.73307 23.4802 15.0834 24.5017 17.4225 24.6195L17.5007 22.766ZM6.36541 16.1805C2.08449 10.7742 1.82142 6.6976 1.96091 4.96382L0.0334113 4.79901C-0.142976 6.98061 0.255096 11.5142 4.83157 17.2915L6.36541 16.1805ZM9.18414 8.86427L9.59982 8.56577L8.43418 7.07537L8.01868 7.37264L9.18414 8.86427ZM10.5162 4.15821L9.19683 1.87122L7.50841 2.7571L8.82791 5.04286L10.5162 4.15821ZM3.84438 0.660984L1.57125 2.28834L2.73927 3.78032L5.01112 2.15279L3.84438 0.660984ZM8.60141 8.11846C8.01612 7.37228 8.01612 7.37228 8.01578 7.37473L8.01323 7.37437L8.00871 7.37873C7.93978 7.42884 7.8764 7.48557 7.81953 7.54807C7.73682 7.63628 7.64342 7.75419 7.55669 7.90798C7.34221 8.3063 7.24 8.75254 7.26012 9.20287C7.28245 10.2855 7.84693 11.7913 9.53449 13.9228L11.0685 12.8106C9.48796 10.8164 9.2024 9.69798 9.19213 9.18737C9.18718 8.94457 9.24328 8.83265 9.25856 8.80734C9.26851 8.7921 9.26886 8.78965 9.25959 8.8C9.24583 8.81528 9.23077 8.82945 9.21457 8.84234L9.20008 8.85278L9.18576 8.862L8.60141 8.11846ZM9.53449 13.9228C11.2233 16.0546 12.5887 16.9853 13.6719 17.3022C14.2259 17.465 14.691 17.4629 15.0544 17.388C15.2578 17.3473 15.4527 17.2752 15.6314 17.1745L15.7029 17.1284L15.7129 17.1223L15.7173 17.1192L15.7189 17.1169C15.7189 17.1169 15.7203 17.1159 15.1363 16.3699C14.551 15.6237 14.555 15.623 14.555 15.623L14.5579 15.621L14.5608 15.6189L14.5694 15.6138L14.5838 15.6034L14.6375 15.5735C14.6505 15.5678 14.6474 15.5682 14.6282 15.5747C14.5948 15.5812 14.4643 15.6016 14.2179 15.5295C13.7002 15.3769 12.6478 14.8046 11.0685 12.8106L9.53449 13.9228ZM9.197 1.87C8.12841 0.0225916 5.60654 -0.601394 3.84438 0.660984L5.01112 2.15279C5.78144 1.60088 6.97743 1.8376 7.50841 2.7571L9.197 1.87ZM1.96202 4.96523C1.99599 4.53821 2.26128 4.12373 2.73909 3.78155L1.5698 2.28938C0.792227 2.84651 0.122048 3.69579 0.0334113 4.79901L1.96202 4.96523ZM18.6846 22.3659C18.2876 22.652 17.8831 22.7873 17.5018 22.7674L17.4225 24.6195C18.3729 24.667 19.204 24.322 19.8526 23.8578L18.6846 22.3659ZM9.59982 8.56577C11.026 7.54445 11.3853 5.66435 10.5173 4.15962L8.82902 5.04427C9.25093 5.77621 9.05271 6.63067 8.43273 7.07641L9.59982 8.56577ZM20.6283 18.3011C21.5953 18.9911 21.5775 20.294 20.742 20.8929L21.9102 22.3836C23.8506 20.9928 23.7055 18.1982 21.7681 16.8165L20.6283 18.3011ZM16.3804 16.6446C16.9364 16.246 17.7417 16.2419 18.3668 16.6866L19.5076 15.2034C18.2249 14.2868 16.4806 14.2451 15.2123 15.1526L16.3804 16.6446Z"
                  fill="#4B3621"
                  fillOpacity="0.8"
                />
                </svg>
              </span>
              <div className="flex flex-col">
                <span className="font-pretendard font-regular text-16px leading-135% text-brown-80%-main">
                  연락처
                </span>
                <span className="font-pretendard font-regular text-12px leading-135% text-textColorText2 mt-2px">
                  오직 친구 찾기 목적으로만 이용돼요
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full px-20px">
          <SubmitButton
            text="다음으로"
            onSubmit={handleNext}
            onDisabled={handleNext}
            loading={loading}
          />
        </div>
      </div>
    </div>
  )
}

export default PermissionRequest
